import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Box,
  Textarea,
  useToast,
  Flex,
  Avatar,
  Spinner,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { useState, useMemo, KeyboardEvent } from 'react';
import {
  useGetOrganizationMembersForSharingQuery,
  useShareProjectMutation,
} from '../../generated/graphql';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanColor } from '../helivan/HelivanColor';
import {
  RiMailLine,
  RiShareLine,
  RiTeamLine,
  RiSearchLine,
  RiCheckLine,
  RiUserAddLine,
  RiAddLine,
} from 'react-icons/ri';

interface ShareProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  organizationId: string;
  currentUserId: string;
}

const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const ShareProjectModal = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  organizationId,
  currentUserId,
}: ShareProjectModalProps) => {
  const toast = useToast();
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [manualEmailInput, setManualEmailInput] = useState('');

  const { data, loading } = useGetOrganizationMembersForSharingQuery({
    variables: { organizationId },
    skip: !isOpen || !organizationId,
  });

  const [shareProject, { loading: sharing }] = useShareProjectMutation();

  // Always use email input mode to protect member privacy
  // Team member emails should not be exposed in the share modal
  const useEmailInputMode = true;

  // Filter out the current user from the members list
  const members = useMemo(() => {
    const allMembers = (data?.getOrganizationWithMembers?.members ?? []).filter(
      (member) => member.id !== currentUserId
    );

    if (!searchQuery.trim()) return allMembers;

    const query = searchQuery.toLowerCase();
    return allMembers.filter(
      (member) =>
        member.fullName.toLowerCase().includes(query) ||
        member.email.toLowerCase().includes(query)
    );
  }, [data, currentUserId, searchQuery]);

  const totalMembers = useMemo(() => {
    return (data?.getOrganizationWithMembers?.members ?? []).filter(
      (member) => member.id !== currentUserId
    ).length;
  }, [data, currentUserId]);

  const handleToggleEmail = (email: string) => {
    setSelectedEmails((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(email)) {
        newSet.delete(email);
      } else {
        newSet.add(email);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedEmails.size === members.length) {
      setSelectedEmails(new Set());
    } else {
      setSelectedEmails(new Set(members.map((m) => m.email)));
    }
  };

  const handleAddManualEmail = () => {
    const email = manualEmailInput.trim().toLowerCase();
    if (!email) return;

    if (!isValidEmail(email)) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (selectedEmails.has(email)) {
      toast({
        title: 'Email already added',
        description: 'This email is already in the list.',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setSelectedEmails((prev) => new Set([...Array.from(prev), email]));
    setManualEmailInput('');
  };

  const handleManualEmailKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddManualEmail();
    }
  };

  const handleRemoveEmail = (email: string) => {
    setSelectedEmails((prev) => {
      const newSet = new Set(prev);
      newSet.delete(email);
      return newSet;
    });
  };

  const handleShare = async () => {
    if (selectedEmails.size === 0) {
      toast({
        title: 'No recipients selected',
        description: 'Please select at least one person to share with.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const result = await shareProject({
        variables: {
          input: {
            projectId,
            recipientEmails: Array.from(selectedEmails),
            message: message.trim() || undefined,
          },
        },
      });

      const { success, sentTo, failedTo } = result.data?.shareProject ?? {
        success: false,
        sentTo: [],
        failedTo: [],
      };

      if (success && sentTo.length > 0) {
        toast({
          title: 'Project shared',
          description: `Successfully sent to ${sentTo.length} ${sentTo.length === 1 ? 'person' : 'people'}.`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });

        if (failedTo.length > 0) {
          toast({
            title: 'Some emails failed',
            description: `Could not send to: ${failedTo.join(', ')}`,
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }

        setSelectedEmails(new Set());
        setMessage('');
        setSearchQuery('');
        setManualEmailInput('');
        onClose();
      } else {
        toast({
          title: 'Failed to share',
          description: 'Could not send the share emails. Please try again.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An error occurred while sharing the project.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => {
    setSelectedEmails(new Set());
    setMessage('');
    setSearchQuery('');
    setManualEmailInput('');
    onClose();
  };

  // Render email input mode for personal orgs
  const renderEmailInputMode = () => (
    <Box
      bg="white"
      borderRadius="12px"
      border="1px solid"
      borderColor={HelivanColor.border.primary}
      overflow="hidden"
    >
      <Flex
        p={4}
        alignItems="center"
        borderBottom="1px solid"
        borderColor={HelivanColor.border.primary}
        bg={HelivanColor.background.secondary}
      >
        <HStack spacing={3}>
          <Flex
            w="36px"
            h="36px"
            borderRadius="10px"
            bg="rgba(0,71,125,0.1)"
            align="center"
            justify="center"
          >
            <Icon as={RiMailLine} boxSize="18px" color={HelivanColor.text.helivanBlue} />
          </Flex>
          <Box>
            <Text fontSize="14px" fontWeight={600} color={HelivanColor.text.blueDark}>
              Share via Email
            </Text>
            <Text fontSize="12px" color={HelivanColor.text.blueMid}>
              Enter email addresses to share with
            </Text>
          </Box>
        </HStack>
      </Flex>

      <Box p={4}>
        <HStack spacing={2} mb={3}>
          <InputGroup size="sm" flex={1}>
            <InputLeftElement>
              <Icon as={RiMailLine} color={HelivanColor.text.blueMid} />
            </InputLeftElement>
            <Input
              placeholder="Enter email address..."
              value={manualEmailInput}
              onChange={(e) => setManualEmailInput(e.target.value)}
              onKeyDown={handleManualEmailKeyDown}
              bg="white"
              borderColor={HelivanColor.border.primary}
              borderRadius="8px"
              _placeholder={{ color: HelivanColor.text.gray }}
              _hover={{ borderColor: HelivanColor.text.helivanBlue }}
              _focus={{
                borderColor: HelivanColor.text.helivanBlue,
                boxShadow: `0 0 0 1px ${HelivanColor.text.helivanBlue}`,
              }}
            />
          </InputGroup>
          <HelivanButton
            variant="primary"
            size="sm"
            onClick={handleAddManualEmail}
            isDisabled={!manualEmailInput.trim()}
            leftIcon={<RiAddLine size={14} />}
          >
            Add
          </HelivanButton>
        </HStack>

        {selectedEmails.size > 0 ? (
          <Box
            p={3}
            bg={HelivanColor.background.secondary}
            borderRadius="8px"
            border="1px solid"
            borderColor={HelivanColor.border.primary}
          >
            <Text fontSize="11px" color={HelivanColor.text.blueMid} mb={2} fontWeight={500}>
              Recipients ({selectedEmails.size})
            </Text>
            <Wrap spacing={2}>
              {Array.from(selectedEmails).map((email) => (
                <WrapItem key={email}>
                  <Tag
                    size="md"
                    borderRadius="6px"
                    variant="subtle"
                    bg="rgba(0,71,125,0.1)"
                    color={HelivanColor.text.blueDark}
                  >
                    <TagLabel fontSize="12px">{email}</TagLabel>
                    <TagCloseButton onClick={() => handleRemoveEmail(email)} />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          </Box>
        ) : (
          <Flex
            direction="column"
            align="center"
            py={6}
            px={4}
            bg={HelivanColor.background.secondary}
            borderRadius="8px"
          >
            <Icon as={RiUserAddLine} boxSize="24px" color={HelivanColor.text.blueMid} mb={2} />
            <Text fontSize="13px" color={HelivanColor.text.blueMid} textAlign="center">
              Add email addresses above to share this project
            </Text>
          </Flex>
        )}
      </Box>
    </Box>
  );

  // Render team member selection mode for organization orgs
  const renderTeamMemberMode = () => (
    <Box
      bg="white"
      borderRadius="12px"
      border="1px solid"
      borderColor={HelivanColor.border.primary}
      overflow="hidden"
    >
      <Flex
        p={4}
        alignItems="center"
        justifyContent="space-between"
        borderBottom="1px solid"
        borderColor={HelivanColor.border.primary}
        bg={HelivanColor.background.secondary}
      >
        <HStack spacing={3}>
          <Flex
            w="36px"
            h="36px"
            borderRadius="10px"
            bg="rgba(0,71,125,0.1)"
            align="center"
            justify="center"
          >
            <Icon as={RiTeamLine} boxSize="18px" color={HelivanColor.text.helivanBlue} />
          </Flex>
          <Box>
            <Text fontSize="14px" fontWeight={600} color={HelivanColor.text.blueDark}>
              Team Members
            </Text>
            <Text fontSize="12px" color={HelivanColor.text.blueMid}>
              {totalMembers} {totalMembers === 1 ? 'member' : 'members'} available
            </Text>
          </Box>
        </HStack>
        {members.length > 0 && (
          <Text
            fontSize="12px"
            color={HelivanColor.text.helivanBlue}
            cursor="pointer"
            onClick={handleSelectAll}
            fontWeight={500}
            _hover={{ textDecoration: 'underline' }}
          >
            {selectedEmails.size === members.length ? 'Deselect all' : 'Select all'}
          </Text>
        )}
      </Flex>

      <Box p={4}>
        {/* Search Input */}
        {totalMembers > 3 && (
          <InputGroup size="sm" mb={3}>
            <InputLeftElement>
              <Icon as={RiSearchLine} color={HelivanColor.text.blueMid} />
            </InputLeftElement>
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg="white"
              borderColor={HelivanColor.border.primary}
              borderRadius="8px"
              _placeholder={{ color: HelivanColor.text.gray }}
              _hover={{ borderColor: HelivanColor.text.helivanBlue }}
              _focus={{
                borderColor: HelivanColor.text.helivanBlue,
                boxShadow: `0 0 0 1px ${HelivanColor.text.helivanBlue}`,
              }}
            />
          </InputGroup>
        )}

        {loading ? (
          <Flex justify="center" py={8}>
            <Spinner size="md" color={HelivanColor.text.helivanBlue} thickness="3px" />
          </Flex>
        ) : totalMembers === 0 ? (
          <Flex direction="column" align="center" py={8} px={4}>
            <Flex
              w="48px"
              h="48px"
              borderRadius="12px"
              bg="rgba(0,71,125,0.08)"
              align="center"
              justify="center"
              mb={3}
            >
              <Icon as={RiUserAddLine} boxSize="24px" color={HelivanColor.text.blueMid} />
            </Flex>
            <Text fontSize="14px" fontWeight={500} color={HelivanColor.text.blueDark}>
              No team members yet
            </Text>
            <Text fontSize="12px" color={HelivanColor.text.blueMid} mt={1} textAlign="center">
              Invite people to your organization to share projects with them.
            </Text>
          </Flex>
        ) : members.length === 0 && searchQuery ? (
          <Flex direction="column" align="center" py={6}>
            <Text fontSize="13px" color={HelivanColor.text.blueMid}>
              No members match "{searchQuery}"
            </Text>
          </Flex>
        ) : (
          <Stack
            spacing={0}
            maxH="220px"
            overflowY="auto"
            borderRadius="8px"
            border="1px solid"
            borderColor={HelivanColor.border.primary}
          >
            {members.map((member, idx) => {
              const isSelected = selectedEmails.has(member.email);
              return (
                <Flex
                  key={member.id}
                  p={3}
                  alignItems="center"
                  cursor="pointer"
                  onClick={() => handleToggleEmail(member.email)}
                  bg={isSelected ? 'rgba(0,71,125,0.06)' : 'white'}
                  _hover={{ bg: isSelected ? 'rgba(0,71,125,0.08)' : 'rgba(0,71,125,0.03)' }}
                  borderBottom={idx < members.length - 1 ? '1px solid' : 'none'}
                  borderColor={HelivanColor.border.primary}
                  transition="background 0.15s ease"
                >
                  <Flex
                    w="20px"
                    h="20px"
                    borderRadius="4px"
                    border="2px solid"
                    borderColor={
                      isSelected ? HelivanColor.text.helivanBlue : HelivanColor.border.primary
                    }
                    bg={isSelected ? HelivanColor.text.helivanBlue : 'white'}
                    align="center"
                    justify="center"
                    mr={3}
                    transition="all 0.15s ease"
                  >
                    {isSelected && <Icon as={RiCheckLine} boxSize="14px" color="white" />}
                  </Flex>
                  <Avatar
                    size="sm"
                    name={member.fullName}
                    mr={3}
                    bg="rgba(0,71,125,0.15)"
                    color={HelivanColor.text.helivanBlue}
                    fontWeight={600}
                  />
                  <Box flex={1}>
                    <Text fontSize="13px" fontWeight={500} color={HelivanColor.text.blueDark}>
                      {member.fullName}
                    </Text>
                    <Text fontSize="11px" color={HelivanColor.text.blueMid}>
                      {member.email}
                    </Text>
                  </Box>
                </Flex>
              );
            })}
          </Stack>
        )}
      </Box>
    </Box>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent bg={HelivanColor.background.secondary} maxH="85vh">
        <ModalHeader
          pb={3}
          bg="white"
          borderBottom="1px solid"
          borderColor={HelivanColor.border.primary}
        >
          <HStack spacing={3}>
            <Flex
              w="40px"
              h="40px"
              borderRadius="10px"
              bg="rgba(0,71,125,0.1)"
              align="center"
              justify="center"
            >
              <Icon as={RiShareLine} boxSize="20px" color={HelivanColor.text.helivanBlue} />
            </Flex>
            <Box>
              <Text fontSize="16px" fontWeight={600} color={HelivanColor.text.blueDark}>
                Share Project
              </Text>
              <Text fontSize="12px" color={HelivanColor.text.blueMid} fontWeight={400}>
                {projectName}
              </Text>
            </Box>
          </HStack>
        </ModalHeader>
        <ModalCloseButton top={4} />
        <ModalBody py={4}>
          {loading ? (
            <Flex justify="center" py={8}>
              <Spinner size="md" color={HelivanColor.text.helivanBlue} thickness="3px" />
            </Flex>
          ) : (
            <Stack spacing={4}>
              {/* Always use email input mode to protect member privacy */}
              {useEmailInputMode ? renderEmailInputMode() : renderTeamMemberMode()}

              {/* Message Section */}
              <Box
                bg="white"
                borderRadius="12px"
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                overflow="hidden"
              >
                <Flex
                  p={4}
                  alignItems="center"
                  borderBottom="1px solid"
                  borderColor={HelivanColor.border.primary}
                  bg={HelivanColor.background.secondary}
                >
                  <HStack spacing={3}>
                    <Flex
                      w="36px"
                      h="36px"
                      borderRadius="10px"
                      bg="rgba(0,71,125,0.1)"
                      align="center"
                      justify="center"
                    >
                      <Icon as={RiMailLine} boxSize="18px" color={HelivanColor.text.helivanBlue} />
                    </Flex>
                    <Box>
                      <Text fontSize="14px" fontWeight={600} color={HelivanColor.text.blueDark}>
                        Personal Message
                      </Text>
                      <Text fontSize="12px" color={HelivanColor.text.blueMid}>
                        Optional note to include in the email
                      </Text>
                    </Box>
                  </HStack>
                </Flex>
                <Box p={4}>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Hey, I thought you might find this project interesting..."
                    size="sm"
                    resize="none"
                    rows={3}
                    bg="white"
                    borderColor={HelivanColor.border.primary}
                    borderRadius="8px"
                    _placeholder={{ color: HelivanColor.text.gray }}
                    _hover={{ borderColor: HelivanColor.text.helivanBlue }}
                    _focus={{
                      borderColor: HelivanColor.text.helivanBlue,
                      boxShadow: `0 0 0 1px ${HelivanColor.text.helivanBlue}`,
                    }}
                  />
                </Box>
              </Box>

              {/* Selection Summary */}
              {selectedEmails.size > 0 && (
                <Flex
                  p={4}
                  bg="rgba(0,71,125,0.08)"
                  borderRadius="12px"
                  alignItems="center"
                  gap={3}
                  border="1px solid"
                  borderColor="rgba(0,71,125,0.15)"
                >
                  <Flex
                    w="32px"
                    h="32px"
                    borderRadius="8px"
                    bg="rgba(0,71,125,0.12)"
                    align="center"
                    justify="center"
                  >
                    <Icon as={RiMailLine} boxSize="16px" color={HelivanColor.text.helivanBlue} />
                  </Flex>
                  <Box flex={1}>
                    <Text fontSize="13px" fontWeight={500} color={HelivanColor.text.blueDark}>
                      Ready to share
                    </Text>
                    <Text fontSize="12px" color={HelivanColor.text.blueMid}>
                      {selectedEmails.size} {selectedEmails.size === 1 ? 'person' : 'people'} will
                      receive an email with a link to this project
                    </Text>
                  </Box>
                </Flex>
              )}
            </Stack>
          )}
        </ModalBody>
        <ModalFooter bg="white" borderTop="1px solid" borderColor={HelivanColor.border.primary}>
          <HStack spacing={3}>
            <HelivanButton variant="outline" onClick={handleClose} size="sm">
              Cancel
            </HelivanButton>
            <HelivanButton
              variant="primary"
              onClick={handleShare}
              isLoading={sharing}
              isDisabled={selectedEmails.size === 0 || loading}
              leftIcon={<RiShareLine size={16} />}
              size="sm"
            >
              Share Project
            </HelivanButton>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
